<template>
  <div v-if="packageList?.length && rightsDisplay?.rights" class="buy-storage">
    <div
      v-if="order.status === undefined"
      class="buy-storage__content"
      :class="{
        'buy-storage__content--column': !isCN,
      }"
    >
      <StorageInfo
        v-if="rightsDisplay?.rights"
        v-model:count="count"
        v-model:currentPackage="data.currentPackage"
        :featureType="RIGHTS_FEATURE.storage"
        :rightsName="currentRight?.rights_name ?? ''"
        :packageList="packageList"
      />
      <template v-if="isCN">
        <div class="buy-storage__divider"></div>
        <PayQrcode
          v-model:pay-type="currentPayType"
          v-model:agreement-checked="agreementAccepted"
          :loading="isCreating"
          :qrcode="qrCode"
          :behavior="ORDER_BEHAVIOR.ADD_PURCHASE"
          :price-text="total"
          :reorder="create"
        >
          <Consultant
            class="buy-storage__consultant"
            v-if="user?.rights_type"
            :rightType="user?.rights_type"
            :orderType="ORDER_TYPE.STORAGE"
            :transferDisabled="!agreementAccepted"
            @transfer="onTransferClick"
          />
        </PayQrcode>
      </template>
      <StripeOperation
        v-else
        :url="raw?.vendor_redirect_url"
        :loading="isCreating"
        @payed="handlePayed"
      >
      </StripeOperation>
    </div>
    <div v-else-if="paySucceeded" class="buy-storage__success">
      <PaySucc
        class="buy-storage__success-main"
        :title="paySuccTitle"
        :desc="paySuccDesc"
        :skip-reload="true"
        @on-success="handlePayed"
      />
      <div class="buy-storage__divider buy-storage__divider--fullheight"></div>
      <PaySuccTip class="buy-storage__success-tip"></PaySuccTip>
    </div>
    <PayFail v-else class="buy-storage__fail" @back="repay()" />
  </div>
  <div v-else class="buy-storage__empty"></div>
</template>

<script setup lang="ts" name="BuyRights">
import { getI18nService } from '@rendora/common/services/i18n';
import { useRightsStore } from '@rendora/common/store/rights';
import { useUserStore } from '@rendora/common/store/user';
import {
  BUY_TYPE,
  IRightsType,
  ORDER_BEHAVIOR,
  ORDER_TYPE,
  RIGHTS_FEATURE,
} from '@rendora/common/types/enums';
import { getGlobalConfig } from '@rendora/common/utils/config';
import { formatPrice } from '@rendora/common/utils/transform';
import { computed, ref } from 'vue';
import type { UseOrderInput } from '../../composables/types';
import useOrder from '../../composables/useOrder';
import useOverseasOrder from '../../composables/useOverseasOrder';
import Consultant from '../Consultant.vue';
import PayFail from '../PayFail.vue';
import PayQrcode from '../PayQrCode.vue';
import PaySucc from '../PaySuccess.vue';
import PaySuccTip from '../PaySuccessTip.vue';
import StripeOperation from '../StripeOperation.vue';
import StorageInfo from './StorageInfo.vue';

const i18nService = getI18nService();
const $t = i18nService.t;

const isCN = getGlobalConfig().isCN;

const { user } = useUserStore();
const { rightsDisplay } = useRightsStore();

const count = ref<number>(1);

export interface IStoragePackage {
  id: number;
  description: string;
  price: number;
  oldPrice: number;
  type: 'GB' | 'TB';
  isGB: boolean;
  min: number;
  max: number;
}

const currentRight = computed(() => {
  return rightsDisplay?.rights?.find(v => v.rights_type === user?.rights_type);
});

const addPrice = computed(() => {
  const ap = currentRight.value?.rights_by_time.find(v => v.id === user?.rights_id)?.add_price;

  return ap;
});

const data = ref<{ currentPackage?: IStoragePackage }>({});

const packageList = computed<IStoragePackage[]>(() => {
  if (!addPrice.value || user?.rights_type === undefined) return [];

  const gb: IStoragePackage = {
    id: 0,
    description: '',
    price: addPrice.value?.add_storage ?? 0,
    oldPrice: addPrice.value?.add_storage_original ?? 0,
    type: 'GB',
    isGB: true,
    min: 1,
    max: 20000,
  };
  if (!data.value.currentPackage) data.value.currentPackage = gb;

  if (
    [IRightsType.PERSONAL, IRightsType.PERSONAL_LITE].includes(user?.rights_type as IRightsType)
  ) {
    return [gb];
  }

  if (
    [IRightsType.ORG_STANDARD, IRightsType.ORG_FLAGSHIP, IRightsType.TEAM_LIGHT].includes(
      user?.rights_type as IRightsType,
    )
  ) {
    return [
      gb,
      {
        id: 1,
        description: '1024GB',
        price: addPrice.value?.add_storage_t ?? 0,
        oldPrice: addPrice.value?.add_storage_t_original ?? 0,
        type: 'TB',
        isGB: false,
        min: 1,
        max: 2000,
      },
    ];
  }
  return [gb];
});

const total = computed<number>(() => {
  const price = data.value.currentPackage?.price ?? 0;
  const qty = Number.isNaN(count.value) ? 1 : count.value;
  return price * qty;
});
const yuanFormat = computed(() => formatPrice(total.value).string);

const paySuccTitle = computed(() => $t('jia-gou-cheng-gong'));
const paySuccDesc = computed(() => {
  const rightsName = currentRight.value?.rights_name ?? '';
  const sizeText = `${count.value}${data.value.currentPackage?.type === 'GB' ? 'GB' : 'TB'}`;
  return $t('rightsnamesizetext-cun-chu-kong-jian-jia-gou-cheng-gong-zhi-fu-yuanformatvalue', [
    rightsName,
    sizeText,
    yuanFormat.value,
  ]);
});

const orderBody = computed(() => {
  return <UseOrderInput>{
    behavior: ORDER_BEHAVIOR.ADD_PURCHASE,
    orderType: ORDER_TYPE.STORAGE,
    buyType: BUY_TYPE.PAYMENT,
    num: count.value,
    currentPackageType: data.value.currentPackage?.type ?? 'UNKNOWN',
    currentPackage: data.value.currentPackage,
  };
});

const orderResult = isCN ? useOrder(orderBody) : useOverseasOrder(orderBody);

const {
  order,
  repay,
  isCreating,
  create,
  raw,
  qrCode,
  agreementAccepted,
  currentPayType,
  paySucceeded,
} = orderResult;

// const {
//   order,
//   repay,
//   isCreating,
//   create,
//   raw,
//   qrCode,
//   agreementAccepted,
//   currentPayType,
//   paySucceeded,
// } = useOrder(orderBody);

const onTransferClick = () => {
  void create(true);
};

const handlePayed = () => location.reload();
</script>

<style scoped lang="scss">
.buy-storage {
  display: grid;
  place-items: center;
  border-radius: var(--x-radius-large);
  background: var(--x-back-1);

  &__content {
    width: 100%;
    display: flex;

    &--column {
      flex-direction: column;
    }
  }

  &__divider {
    width: 1px;
    background: #f1f1f1;

    &--fullheight {
      height: 100%;
    }
  }

  &__consultant {
    margin-top: 20px;
  }

  &__success {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  &__success-main {
    flex: 1 1 auto;
    padding-top: 173px;
    padding-bottom: 173px;
  }

  &__success-tip {
    min-width: 254px;
  }

  &__fail {
    padding-top: 173px;
    padding-bottom: 173px;
  }

  &__empty {
    display: flex;
    align-items: center;
    width: 100%;
    height: 565px;
  }
}
</style>
